/**
 * Editor in-place de PedidoAmbiente + PedidoItem.
 *
 * Reusa visual + interacao de EditorAmbientes do orcamento, mas chama
 * services especificos do pedido. Gate: `editavel` controlado pelo caller
 * (deve corresponder a `pedido_aceita_edicao_de_itens` do backend:
 * revisao_em_andamento OR situacao=RECEBIDO).
 */
import React, { useState } from "react";
import { FiPlus, FiSave, FiTrash2 } from "react-icons/fi";

import { useAuth } from "../../context/AuthContext";
import {
  atualizarPedidoAmbiente,
  atualizarPedidoItem,
  criarPedidoAmbiente,
  criarPedidoItem,
  deletarPedidoAmbiente,
  deletarPedidoItem,
} from "../../services/pedidos";

import { fmtMoeda } from "../Orcamentos/helpers";
import {
  AmbienteCard,
  AmbienteHeader,
  EmptyMsg,
  ErrorMsg,
  IconButton,
  Input,
  ItemRow,
  ItemTotal,
  PrimaryButton,
  SecondaryButton,
  Select,
} from "./styles";

const UNIDADES_MEDIDA = ["UN", "M", "M2", "M3", "KG", "PC", "PAR", "CJ"];

function calcItemTotal(item, { editado = false } = {}) {
  // O backend ja resolve o total da linha (`valor_total`): sob o motor
  // divisor ele vem do preco alocado, e o unitario e um derivado
  // arredondado — recalcular aqui devolvia R$ 80.000,01 num pedido de
  // R$ 80.000,00 (auditoria 2026-08-07, CRIT-1).
  //
  // `editado` = o usuario mexeu em quantidade/valor e ainda nao salvou.
  // Nesse caso o total do servidor esta velho e a multiplicacao e a
  // previa correta — e tambem o que o backend vai gravar, porque editar
  // esses campos reabre a linha (limpa o preco alocado).
  if (!editado && item.valor_total != null && item.valor_total !== "") {
    return Number(item.valor_total);
  }
  return Number(item.quantidade || 0) * Number(item.valor_unitario || 0);
}

function novoItemVazio() {
  return {
    descricao: "",
    quantidade: "1",
    unidade_medida: "UN",
    valor_unitario: "0.00",
    ordem: 0,
  };
}

function ItemEditor({ item, onSave, onDelete, editavel }) {
  const [valores, setValores] = useState(item);
  const [dirty, setDirty] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const setCampo = (campo, valor) => {
    setValores((p) => ({ ...p, [campo]: valor }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSalvando(true);
    try {
      await onSave(valores);
      setDirty(false);
    } finally {
      setSalvando(false);
    }
  };

  const total = calcItemTotal(valores, { editado: dirty });

  // V30 (auditoria 2026-08-07): o descritivo aprovado no orcamento
  // (puxador, mecanismo, dimensoes, material, acabamento) viaja no pedido
  // desde a conversao — aqui ele volta a ficar VISIVEL, em linha secundaria
  // sob a descricao, sem poluir as colunas de valor.
  const detalhes = [];
  // Dimensao ausente vira "?" no lugar de sumir: "600 × 500 mm" nao diz
  // quais eixos sobraram (largura? altura?).
  const eixos = [valores.largura_mm, valores.altura_mm, valores.profundidade_mm]
    .map((v) => (v == null || v === "" || Number(v) <= 0 ? null : Math.round(Number(v))));
  if (eixos.some((v) => v != null)) {
    detalhes.push(`${eixos.map((v) => (v == null ? "?" : v)).join(" × ")} mm (L×A×P)`);
  }
  if (valores.material) detalhes.push(valores.material);
  if (valores.acabamento) detalhes.push(valores.acabamento);
  if (valores.puxador) detalhes.push(`puxador ${valores.puxador}`);
  if (valores.mecanismo) detalhes.push(`mecanismo ${valores.mecanismo}`);

  return (
    <ItemRow>
      <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        <Input
          type="text"
          value={valores.descricao}
          onChange={(e) => setCampo("descricao", e.target.value)}
          disabled={!editavel}
        />
        {detalhes.length > 0 && (
          <span
            className="tiny muted"
            title={detalhes.join(" · ")}
            style={{
              paddingLeft: 8,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {detalhes.join(" · ")}
          </span>
        )}
      </div>
      <Input
        type="number" step="0.001" min="0"
        value={valores.quantidade}
        onChange={(e) => setCampo("quantidade", e.target.value)}
        disabled={!editavel}
      />
      <Select
        value={valores.unidade_medida}
        onChange={(e) => setCampo("unidade_medida", e.target.value)}
        disabled={!editavel}
      >
        {UNIDADES_MEDIDA.map((u) => <option key={u} value={u}>{u}</option>)}
      </Select>
      <Input
        type="number" step="0.01" min="0"
        value={valores.valor_unitario}
        onChange={(e) => setCampo("valor_unitario", e.target.value)}
        disabled={!editavel}
      />
      <Input
        type="text" readOnly tabIndex={-1}
        value={fmtMoeda(total)}
        style={{ background: "transparent", border: 0, outline: "none", pointerEvents: "none", fontWeight: 600, textAlign: "right" }}
      />
      {editavel && (
        <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
          {dirty && (
            <IconButton type="button" onClick={handleSave} disabled={salvando} title="Salvar alterações">
              <FiSave size={12} />
            </IconButton>
          )}
          <IconButton type="button" onClick={onDelete} title="Remover" disabled={salvando}>
            <FiTrash2 size={12} />
          </IconButton>
        </div>
      )}
    </ItemRow>
  );
}

function AmbienteEditor({ ambiente, editavel, onChange }) {
  const { request } = useAuth();
  const [nome, setNome] = useState(ambiente.nome);
  const [dirtyNome, setDirtyNome] = useState(false);
  const [adicionando, setAdicionando] = useState(false);
  const [novoItem, setNovoItem] = useState(novoItemVazio());
  const [erro, setErro] = useState(null);

  const handleSalvarNome = async () => {
    setErro(null);
    try {
      await atualizarPedidoAmbiente(request, ambiente.id, { nome });
      setDirtyNome(false);
      await onChange();
    } catch (err) {
      setErro(err?.message || "Erro ao salvar nome.");
    }
  };

  const handleDeletarAmbiente = async () => {
    if (!window.confirm(`Remover ambiente "${ambiente.nome}" e todos os itens?`)) {
      return;
    }
    setErro(null);
    try {
      await deletarPedidoAmbiente(request, ambiente.id);
      await onChange();
    } catch (err) {
      setErro(err?.message || "Erro ao remover.");
    }
  };

  const handleSalvarItem = async (item) => {
    setErro(null);
    try {
      await atualizarPedidoItem(request, item.id, {
        ambiente: ambiente.id,
        descricao: item.descricao,
        quantidade: item.quantidade,
        unidade_medida: item.unidade_medida,
        valor_unitario: item.valor_unitario,
        ordem: item.ordem,
      });
      await onChange();
    } catch (err) {
      setErro(err?.message || "Erro ao salvar item.");
    }
  };

  const handleDeletarItem = async (itemId) => {
    setErro(null);
    try {
      await deletarPedidoItem(request, itemId);
      await onChange();
    } catch (err) {
      setErro(err?.message || "Erro ao remover item.");
    }
  };

  const handleCriarItem = async () => {
    if (!novoItem.descricao.trim()) {
      setErro("Descrição do item eh obrigatória.");
      return;
    }
    setErro(null);
    try {
      await criarPedidoItem(request, {
        ambiente: ambiente.id,
        ...novoItem,
        ordem: ambiente.itens?.length || 0,
      });
      setNovoItem(novoItemVazio());
      setAdicionando(false);
      await onChange();
    } catch (err) {
      setErro(err?.message || "Erro ao criar item.");
    }
  };

  return (
    <AmbienteCard>
      <AmbienteHeader>
        <Input
          type="text"
          value={nome}
          onChange={(e) => { setNome(e.target.value); setDirtyNome(true); }}
          disabled={!editavel}
          style={{ fontWeight: 600, fontSize: "1rem", flex: 1 }}
        />
        {editavel && (
          <>
            {dirtyNome && (
              <IconButton type="button" onClick={handleSalvarNome} title="Salvar">
                <FiSave size={12} />
              </IconButton>
            )}
            <IconButton type="button" onClick={handleDeletarAmbiente} title="Remover ambiente">
              <FiTrash2 size={12} />
            </IconButton>
          </>
        )}
      </AmbienteHeader>

      {ambiente.itens?.length === 0 && !adicionando && (
        <EmptyMsg style={{ padding: 8 }}>Nenhum item neste ambiente.</EmptyMsg>
      )}

      {ambiente.itens?.length > 0 && (
        <ItemRow style={{ borderTop: 0, fontWeight: 600, opacity: 0.6, fontSize: "0.7rem" }}>
          <div>DESCRICAO</div>
          <div>QTD</div>
          <div>UN</div>
          <div>VL UNIT</div>
          <div style={{ textAlign: "right" }}>TOTAL</div>
          <div />
        </ItemRow>
      )}

      {(ambiente.itens || []).map((item) => (
        <ItemEditor
          key={item.id}
          item={item}
          editavel={editavel}
          onSave={handleSalvarItem}
          onDelete={() => handleDeletarItem(item.id)}
        />
      ))}

      {adicionando && (
        <ItemRow style={{ background: "rgba(194,104,58,0.06)" }}>
          <Input type="text" autoFocus
            value={novoItem.descricao}
            onChange={(e) => setNovoItem((p) => ({ ...p, descricao: e.target.value }))}
            placeholder="Descrição"
          />
          <Input type="number" step="0.001" min="0"
            value={novoItem.quantidade}
            onChange={(e) => setNovoItem((p) => ({ ...p, quantidade: e.target.value }))}
          />
          <Select
            value={novoItem.unidade_medida}
            onChange={(e) => setNovoItem((p) => ({ ...p, unidade_medida: e.target.value }))}
          >
            {UNIDADES_MEDIDA.map((u) => <option key={u} value={u}>{u}</option>)}
          </Select>
          <Input type="number" step="0.01" min="0"
            value={novoItem.valor_unitario}
            onChange={(e) => setNovoItem((p) => ({ ...p, valor_unitario: e.target.value }))}
          />
          <Input type="text" readOnly tabIndex={-1}
            value={fmtMoeda(calcItemTotal(novoItem))}
            style={{ background: "transparent", border: 0, outline: "none", pointerEvents: "none", fontWeight: 600, textAlign: "right" }}
          />
          <div style={{ display: "flex", gap: 4 }}>
            <IconButton type="button" onClick={handleCriarItem} title="Confirmar">
              <FiSave size={12} />
            </IconButton>
            <IconButton type="button"
              onClick={() => { setAdicionando(false); setNovoItem(novoItemVazio()); }}
              title="Cancelar"
            >
              <FiTrash2 size={12} />
            </IconButton>
          </div>
        </ItemRow>
      )}

      {editavel && !adicionando && (
        <SecondaryButton type="button" onClick={() => setAdicionando(true)}
          style={{ alignSelf: "flex-start" }}
        >
          <FiPlus size={12} /> Adicionar item
        </SecondaryButton>
      )}

      {erro && <ErrorMsg>{erro}</ErrorMsg>}
    </AmbienteCard>
  );
}

function EditorPedidoAmbientes({ pedido, editavel, onChange }) {
  const { request } = useAuth();
  const [criandoAmbiente, setCriandoAmbiente] = useState(false);
  const [nomeNovoAmbiente, setNomeNovoAmbiente] = useState("");
  const [erro, setErro] = useState(null);

  const handleCriarAmbiente = async () => {
    if (!nomeNovoAmbiente.trim()) {
      setErro("Nome do ambiente eh obrigatório.");
      return;
    }
    setErro(null);
    try {
      await criarPedidoAmbiente(request, {
        pedido: pedido.id,
        nome: nomeNovoAmbiente.trim(),
        ordem: pedido.ambientes?.length || 0,
      });
      setNomeNovoAmbiente("");
      setCriandoAmbiente(false);
      await onChange();
    } catch (err) {
      setErro(err?.message || "Erro ao criar ambiente.");
    }
  };

  const ambientes = pedido.ambientes || [];
  const totalGeral = ambientes.reduce(
    (acc, amb) => acc + (amb.itens || []).reduce((s, it) => s + calcItemTotal(it), 0),
    0,
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {ambientes.length === 0 && !criandoAmbiente && (
        <EmptyMsg>
          Nenhum ambiente. {editavel && "Use 'Adicionar ambiente' abaixo."}
        </EmptyMsg>
      )}

      {ambientes.map((amb) => (
        <AmbienteEditor
          key={amb.id}
          ambiente={amb}
          editavel={editavel}
          onChange={onChange}
        />
      ))}

      {criandoAmbiente && (
        <AmbienteCard>
          <AmbienteHeader>
            <Input type="text" autoFocus
              value={nomeNovoAmbiente}
              onChange={(e) => setNomeNovoAmbiente(e.target.value)}
              placeholder="Nome (ex.: Cozinha, Sala TV)"
              style={{ fontWeight: 600, fontSize: "1rem" }}
            />
            <IconButton type="button" onClick={handleCriarAmbiente} title="Criar">
              <FiSave size={12} />
            </IconButton>
            <IconButton type="button"
              onClick={() => { setCriandoAmbiente(false); setNomeNovoAmbiente(""); }}
              title="Cancelar"
            >
              <FiTrash2 size={12} />
            </IconButton>
          </AmbienteHeader>
          {erro && <ErrorMsg>{erro}</ErrorMsg>}
        </AmbienteCard>
      )}

      {editavel && !criandoAmbiente && (
        <PrimaryButton type="button" onClick={() => setCriandoAmbiente(true)}
          style={{ alignSelf: "flex-start" }}
        >
          <FiPlus size={14} /> Adicionar ambiente
        </PrimaryButton>
      )}

      {ambientes.length > 0 && (
        <ItemTotal>Total geral: {fmtMoeda(totalGeral)}</ItemTotal>
      )}
    </div>
  );
}

export default EditorPedidoAmbientes;
